import React from 'react';
import Header from '../../components/ui/Header';
import Seo from '../../components/Seo';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DemoVideoFrame from '../../components/home/DemoVideoFrame';
import { appendTrackingParams } from '../../utils/tracking';

const CharityPage = () => {
  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com').replace(/\/$/, '');

  const featureCards = [
    {
      title: 'Donation tracking that stays clean',
      description:
        'Record one-off and recurring gifts, match payments to donors, and keep every donation audit-ready.',
      icon: 'Heart',
      tone: 'bg-rose-500/10 text-rose-600 border-rose-200/60',
    },
    {
      title: 'Gift Aid claims, built in',
      description:
        'Capture eligible declarations and prepare Gift Aid claim data without spreadsheets or manual checks.',
      icon: 'Gift',
      tone: 'bg-amber-500/10 text-amber-700 border-amber-200/70',
    },
    {
      title: 'Monthly donation trend tracker',
      description:
        'See momentum at a glance with month-by-month donation trends and confidence in your forecasting.',
      icon: 'BarChart3',
      tone: 'bg-emerald-500/10 text-emerald-700 border-emerald-200/70',
    },
  ];

  const highlightItems = [
    'Purpose-built donation and Gift Aid workflows',
    'Clean records for trustees and compliance reviews',
    'Real-time visibility into supporter momentum',
  ];

  return (
    <>
      <Seo
        title="Charity Accounting Software | Neevii"
        description="Track donations, manage Gift Aid, and monitor monthly giving trends with Neevii Charity Mode."
      />
      <div className="min-h-screen text-foreground">
        <Header />
        <main className="pt-24 pb-16">
          {/* Hero */}
          <section className="relative overflow-hidden">
            <div
              className="absolute inset-0 bg-center bg-cover"
              style={{
                backgroundImage: "url('/assets/images/Neevi_landing_page_light_background.png')",
              }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-white/85 via-white/75 to-[#e7ecf4]/70" />
            <div className="relative max-w-7xl xl:max-w-[1600px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-8 py-14 lg:py-20">
              <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-14 items-center">
                <div className="space-y-7 text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#942561] text-xs sm:text-sm font-semibold tracking-wide text-white shadow-sm">
                    <Icon name="Heart" size={16} className="text-pink-200" />
                    Charity Mode
                  </div>
                  <h1 className="hero-heading font-normal tracking-tight drop-shadow-sm text-slate-900">
                    <span style={{ color: '#965ffd' }}>Donations</span> and{' '}
                    <span style={{ color: '#17a34a' }}>Gift Aid</span>, made effortless for charities
                  </h1>
                  <p className="text-lg sm:text-xl text-slate-700 max-w-2xl mx-auto lg:mx-0">
                    Neevii helps charities track donations, manage Gift Aid claims, and monitor monthly donation trends in one calm, reliable workspace.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                    <Button
                      variant="default"
                      size="lg"
                      className="bg-brand text-slate-900 shadow-xl shadow-brand/25 hover:shadow-brand/40"
                      onClick={() => {
                        const separator = appUrl.includes('?') ? '&' : '?';
                        const params = new URLSearchParams({
                          plan: 'beta',
                          utm_source: 'website',
                          utm_campaign: 'charity_page',
                          utm_content: 'primary_cta',
                        });
                        window.location.href = appendTrackingParams(`${appUrl}${separator}${params.toString()}`);
                      }}
                      iconName="Zap"
                      iconPosition="left"
                    >
                      Request charity access
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-slate-300 bg-white text-slate-900 hover:bg-slate-50 hover:text-slate-900"
                      onClick={() => {
                        const mailUrl =
                          'https://mail.google.com/mail/?view=cm&fs=1&to=support@neevii.com&su=Neevii%20Charity%20Mode%20enquiry';
                        window.open(mailUrl, '_blank', 'noopener,noreferrer');
                      }}
                      iconName="Mail"
                      iconPosition="left"
                    >
                      Talk to the founder
                    </Button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3 text-sm text-slate-700">
                    {highlightItems.map((item) => (
                      <div key={item} className="flex items-center gap-2 justify-center lg:justify-start">
                        <Icon name="CheckCircle" size={16} className="text-emerald-600" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative flex justify-center lg:justify-end">
                  <DemoVideoFrame
                    className="w-full max-w-2xl lg:max-w-none"
                    src="/assets/videos/Charity_overview.mp4"
                    fit="fill"
                    useMaskInset={false}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-16 lg:py-20 bg-background">
            <div className="max-w-7xl xl:max-w-[1600px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                  Focus on your mission, not admin
                </h2>
                <p className="text-lg text-muted-foreground">
                  Charity Mode is tuned for donations, Gift Aid, and board-ready reporting so you can spend more time supporting your community.
                </p>
              </div>

              <div className="mt-12 grid gap-6 lg:grid-cols-3">
                {featureCards.map((feature) => (
                  <div
                    key={feature.title}
                    className="bg-card/70 backdrop-blur-sm border border-border rounded-2xl p-6 shadow-sm"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${feature.tone}`}>
                      <Icon name={feature.icon} size={22} />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-foreground">{feature.title}</h3>
                    <p className="mt-3 text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#e9eef6]">
            <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
              <div className="bg-white/80 border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-lg shadow-slate-200/40">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Ready to see Charity Mode in action?
                </h2>
                <p className="mt-4 text-lg text-slate-700">
                  Get early access and we’ll help you set up donations, Gift Aid, and monthly trend tracking from day one.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-brand text-slate-900"
                    onClick={() => {
                      const separator = appUrl.includes('?') ? '&' : '?';
                      const params = new URLSearchParams({
                        plan: 'beta',
                        utm_source: 'website',
                        utm_campaign: 'charity_page',
                        utm_content: 'cta_footer',
                      });
                      window.location.href = appendTrackingParams(`${appUrl}${separator}${params.toString()}`);
                    }}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Get early access
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-slate-300 bg-white text-slate-900 hover:bg-slate-50 hover:text-slate-900"
                    onClick={() => {
                      window.location.href = 'mailto:support@neevii.com?subject=Neevii%20Charity%20Mode%20enquiry';
                    }}
                    iconName="HelpCircle"
                    iconPosition="left"
                  >
                    Ask a question
                  </Button>
                </div>
              </div>
            </div>
          </section>
          <style jsx>{`
            .hero-heading {
              font-size: clamp(1.75rem, 4vw, 3.2rem);
              line-height: 1.15;
            }
          `}</style>
        </main>
      </div>
    </>
  );
};

export default CharityPage;
